import React, { useEffect, useMemo, useRef } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import {
  Routes,
  Route,
  useParams,
  useMatch,
  useNavigate,
  Navigate,
} from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'

import { XStack, YStack } from 'tamagui'

import { wrapSuspenseScreen } from '@components/SuspenseWrapper'

import { Group } from '@newComponents/Group'
import { Icon } from '@newComponents/Icon'
import { IconWrapper } from '@newComponents/Icon/IconWrapper'
import { Link } from '@newComponents/Link'
import { SettingsLayout } from '@newComponents/SettingsLayout'
import { Text } from '@newComponents/Text'

import { useDynamicIsland } from '@newComponents/DynamicIsland/useDynamicIsland'
import { FlashContent } from '@newComponents/DynamicIsland/FlashContent'

import {
  SettingsEditProvider,
  useSettingsEdit,
} from '@newComponents/SettingsEdit'
import {
  SettingsBackLink,
  SettingsSaveButton,
  SettingsStatusText,
  setOnSaveAndNavigate,
} from '@newComponents/SettingsEdit/SettingsEditDynamicIslandRow'
import {
  useSettingsNavigationGuard,
  clearGuardPendingState,
} from '@newComponents/SettingsEdit/useSettingsNavigationGuard'
import { UnsavedChangesDialog } from '@newComponents/SettingsEdit/UnsavedChangesDialog'

import {
  GuildSettingsNavigatorQuery,
  GuildMembershipLevel,
} from './__generated__/GuildSettingsNavigatorQuery.graphql'

const GuildEditGeneralScreen = wrapSuspenseScreen(
  loadable(
    () => import('@screens/GuildEditGeneralScreen/GuildEditGeneralScreen')
  )
)

const GuildEditMembersScreen = wrapSuspenseScreen(
  loadable(
    () => import('@screens/GuildEditMembersScreen/GuildEditMembersScreen')
  )
)

const GuildSettingsMessageScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import('@screens/GuildSettingsMessageScreen/GuildSettingsMessageScreen')
  )
)

const GuildEditNetworkScreen = wrapSuspenseScreen(
  loadable(
    () => import('@screens/GuildEditNetworkScreen/GuildEditNetworkScreen')
  )
)

const GuildEditMeetupScreen = wrapSuspenseScreen(
  loadable(() => import('@screens/GuildEditMeetupScreen/GuildEditMeetupScreen'))
)

const GuildSettingsSlackScreen = wrapSuspenseScreen(
  loadable(
    () => import('@screens/GuildSettingsSlackScreen/GuildSettingsSlackScreen')
  )
)

const GuildSettingsBlueskyScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import('../screens/GuildSettingsBlueskyScreen/GuildSettingsBlueskyScreen')
  )
)

const GuildSettingsMonetizationScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import(
        '@screens/GuildSettingsMonetizationScreen/GuildSettingsMonetizationScreen'
      )
  )
)

const GuildSettingsSponsorshipScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import(
        '@screens/GuildSettingsSponsorshipScreen/GuildSettingsSponsorshipScreen'
      )
  )
)

const GuildSettingsMyMembershipScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import(
        '@screens/GuildSettingsMyMembershipScreen/GuildSettingsMyMembershipScreen'
      )
  )
)

const GuildSettingsLeaveScreen = wrapSuspenseScreen(
  loadable(
    () => import('@screens/GuildSettingsLeaveScreen/GuildSettingsLeaveScreen')
  )
)

const GuildSettingsNotificationsScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import(
        '@screens/GuildSettingsNotificationsScreen/GuildSettingsNotificationsScreen'
      )
  )
)

const GuildSettingsEmbedsScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import(
        '@screens/GuildSettingsEmbedsScreen/GuildSettingsEmbedsScreen'
      )
  )
)

const BlueskyIcon = loadable(
  () => import('../newComponents/Icon/icons/BlueskyIcon')
)

export const GuildSettingsNavigator = () => {
  const params = useParams()

  const data = useLazyLoadQuery<GuildSettingsNavigatorQuery>(
    graphql`
      query GuildSettingsNavigatorQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on Guild {
            myMembership {
              membershipLevel
            }

            sponsorshipApproved
          }
        }
      }
    `,
    { id: params.actorId ?? '' }
  )

  if (
    data.nodeBySlugId?.__typename !== 'Guild' ||
    !data.nodeBySlugId.myMembership
  ) {
    return <Navigate to={`/${params.actorId}`} />
  }

  const { membershipLevel } = data.nodeBySlugId.myMembership
  const sponsorshipApproved = data.nodeBySlugId.sponsorshipApproved ?? null

  if (membershipLevel === 'ORGANIZER') {
    return (
      <SettingsEditProvider settingsId='guild' saveOrder={['general']}>
        <GuildSettingsOrganizerContent
          sponsorshipApproved={sponsorshipApproved}
        />
      </SettingsEditProvider>
    )
  }

  return (
    <SettingsLayout
      sidebar={
        <NavigationButtons
          membershipLevel={membershipLevel}
          sponsorshipApproved={sponsorshipApproved}
        />
      }
    >
      <NavigationStack
        membershipLevel={membershipLevel}
        sponsorshipApproved={sponsorshipApproved}
      />
    </SettingsLayout>
  )
}

const GuildSettingsOrganizerContent = ({
  sponsorshipApproved,
}: {
  sponsorshipApproved: boolean | null
}) => {
  const params = useParams()
  const navigate = useNavigate()
  const { pushRows, popRows } = useDynamicIsland()
  const { saveAll, getPendingValues } = useSettingsEdit()

  const guildUrl = `/${params.actorId}`

  // When save-and-navigate fires we must skip the popRows() cleanup,
  // otherwise it overwrites the flash that was just pushed.
  const skipPopRef = useRef(false)

  // Keep refs so the callback always has fresh values
  const saveAllRef = useRef(saveAll)
  saveAllRef.current = saveAll
  const getPendingValuesRef = useRef(getPendingValues)
  getPendingValuesRef.current = getPendingValues
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const guildUrlRef = useRef(guildUrl)
  guildUrlRef.current = guildUrl
  const pushRowsRef = useRef(pushRows)
  pushRowsRef.current = pushRows
  const popRowsRef = useRef(popRows)
  popRowsRef.current = popRows

  // Settings DI rows -- stored in a ref so the flash handler can restore them
  const settingsRows = useMemo(
    () => [
      [
        {
          id: 'guild-settings-back',
          component: (
            <SettingsBackLink href={guildUrl} label='Guild Settings' />
          ),
        },
        {
          id: 'guild-settings-status',
          component: <SettingsStatusText settingsId='guild' />,
          containerStyle: {
            flex: 1,
            justifyContent: 'center' as const,
            alignItems: 'center' as const,
          },
        },
        {
          id: 'guild-settings-save',
          component: <SettingsSaveButton settingsId='guild' />,
        },
      ],
    ],
    [guildUrl]
  )
  const settingsRowsRef = useRef(settingsRows)
  settingsRowsRef.current = settingsRows

  // Register save-and-navigate callback for the DI Save button
  useEffect(() => {
    setOnSaveAndNavigate(async () => {
      // Read the new slug from pending values BEFORE save clears them
      const pendingGeneral = getPendingValuesRef.current('general') as
        | { slugId?: string }
        | undefined
      const newSlug = pendingGeneral?.slugId

      let saved = false

      try {
        await saveAllRef.current()
        saved = true
      } catch {
        // handled below
      }

      if (saved) {
        clearGuardPendingState()
        skipPopRef.current = true

        // Navigate to the new slug if it was changed, otherwise the current URL
        const targetUrl = newSlug ? `/${newSlug}` : guildUrlRef.current

        pushRowsRef.current([
          [
            {
              id: `flash-success-${Date.now()}`,
              meta: { flashType: 'success' },
              component: (
                <FlashContent
                  message='Guild settings saved'
                  type='success'
                  onDismiss={() => {
                    popRowsRef.current()
                  }}
                />
              ),
            },
          ],
        ])

        // Navigate after a brief delay to let the flash animation start
        setTimeout(() => {
          navigateRef.current(targetUrl)
        }, 100)

        // Auto-dismiss flash after 4s (restores default nav rows)
        setTimeout(() => {
          popRowsRef.current()
        }, 4000)
      } else {
        pushRowsRef.current([
          [
            {
              id: `flash-error-${Date.now()}`,
              meta: { flashType: 'error' },
              component: (
                <FlashContent
                  message='Guild settings failed to save'
                  type='error'
                  onDismiss={() => {
                    pushRowsRef.current(settingsRowsRef.current)
                  }}
                />
              ),
            },
          ],
        ])
        setTimeout(() => {
          pushRowsRef.current(settingsRowsRef.current)
        }, 4000)
      }
    })

    return () => {
      setOnSaveAndNavigate(null)
    }
  }, [])

  useEffect(() => {
    pushRows(settingsRows)

    return () => {
      if (!skipPopRef.current) {
        popRows()
      }
    }
  }, [])

  const { isBlocked, handleSaveAndLeave, handleDiscardAndLeave, handleCancel } =
    useSettingsNavigationGuard({ pathPattern: /^(\/[^/]+\/settings)/ })

  return (
    <>
      <SettingsLayout
        sidebar={
          <NavigationButtons
            membershipLevel='ORGANIZER'
            sponsorshipApproved={sponsorshipApproved}
          />
        }
      >
        <NavigationStack
          membershipLevel='ORGANIZER'
          sponsorshipApproved={sponsorshipApproved}
        />
      </SettingsLayout>
      {isBlocked && (
        <UnsavedChangesDialog
          onSaveAndLeave={handleSaveAndLeave}
          onDiscard={handleDiscardAndLeave}
          onCancel={handleCancel}
        />
      )}
    </>
  )
}

const NavItem = ({
  href,
  icon,
  label,
  isActive,
}: {
  href: string
  icon: React.ReactNode
  label: string
  isActive: boolean
}) => (
  <Link
    href={href}
    hoverStyle={{ textDecorationLine: 'none' }}
    paddingHorizontal='$5'
    paddingVertical='$2'
  >
    <XStack alignItems='center' gap='$3'>
      {icon}
      <Text
        color={isActive ? '$darkBlue10' : '$grey8'}
        fontWeight={isActive ? '$6' : '$5'}
      >
        {label}
      </Text>
    </XStack>
  </Link>
)

const OrganizerNavigationButtons = ({
  sponsorshipApproved,
}: {
  sponsorshipApproved: boolean | null
}) => {
  const params = useParams()

  // useMatch matches against location data which encodes any unicode slugs
  const actorIdMatch = encodeURIComponent(params.actorId ?? '')
  const generalMatch = useMatch(`/${actorIdMatch}/settings`)
  const membersMatch = useMatch(`/${actorIdMatch}/settings/members`)
  const messageMatch = useMatch(`/${actorIdMatch}/settings/message`)
  const networkMatch = useMatch(`/${actorIdMatch}/settings/network`)
  const monetizationMatch = useMatch(`/${actorIdMatch}/settings/monetization`)
  const sponsorshipMatch = useMatch(`/${actorIdMatch}/settings/sponsorship`)
  const blueskyMatch = useMatch(`/${actorIdMatch}/settings/bluesky`)
  const slackMatch = useMatch(`/${actorIdMatch}/settings/slack`)
  const meetupMatch = useMatch(`/${actorIdMatch}/settings/meetup`)
  const notificationsMatch = useMatch(`/${actorIdMatch}/settings/notifications`)
  const embedsMatch = useMatch(`/${actorIdMatch}/settings/embeds`)

  // Build items list with dynamic indices for conditional items
  const items = useMemo(() => {
    const result: {
      href: string
      icon: string | 'bluesky'
      label: string
      match: ReturnType<typeof useMatch>
    }[] = [
      {
        href: `/${params.actorId}/settings`,
        icon: 'home-filled',
        label: 'General',
        match: generalMatch,
      },
      {
        href: `/${params.actorId}/settings/members`,
        icon: 'people-community',
        label: 'Members',
        match: membersMatch,
      },
      {
        href: `/${params.actorId}/settings/message`,
        icon: 'mail',
        label: 'Message',
        match: messageMatch,
      },
      {
        href: `/${params.actorId}/settings/network`,
        icon: 'organization',
        label: 'Network',
        match: networkMatch,
      },
      {
        href: `/${params.actorId}/settings/monetization`,
        icon: 'money',
        label: 'Monetization',
        match: monetizationMatch,
      },
      {
        href: `/${params.actorId}/settings/embeds`,
        icon: 'window-new',
        label: 'Embeds',
        match: embedsMatch,
      },
    ]

    if (sponsorshipApproved) {
      result.push({
        href: `/${params.actorId}/settings/sponsorship`,
        icon: 'globe-location',
        label: 'Sponsorship',
        match: sponsorshipMatch,
      })
    }

    result.push(
      {
        href: `/${params.actorId}/settings/bluesky`,
        icon: 'bluesky',
        label: 'Bluesky',
        match: blueskyMatch,
      },
      {
        href: `/${params.actorId}/settings/slack`,
        icon: 'slack',
        label: 'Slack',
        match: slackMatch,
      },
      {
        href: `/${params.actorId}/settings/meetup`,
        icon: 'meetup',
        label: 'Meetup',
        match: meetupMatch,
      },
      {
        href: `/${params.actorId}/settings/notifications`,
        icon: 'mail-inbox-filled',
        label: 'Notifications',
        match: notificationsMatch,
      }
    )

    return result
  }, [
    params.actorId,
    sponsorshipApproved,
    generalMatch,
    membersMatch,
    messageMatch,
    networkMatch,
    monetizationMatch,
    sponsorshipMatch,
    blueskyMatch,
    slackMatch,
    meetupMatch,
    notificationsMatch,
    embedsMatch,
  ])

  const activeIndex = useMemo(() => {
    const index = items.findIndex((item) => item.match !== null)
    return index >= 0 ? index : undefined
  }, [items])

  return (
    <Group
      activeIndex={activeIndex}
      gap='$2'
      fullWidthHighlight
      highlightedElementProps={{ borderRadius: '$6' }}
    >
      {items.map((item, index) => (
        <Group.Item key={item.href} index={index}>
          <NavItem
            href={item.href}
            isActive={item.match !== null}
            label={item.label}
            icon={
              item.icon === 'bluesky' ? (
                <IconWrapper
                  size='$5'
                  color={item.match ? '$darkBlue10' : '$grey8'}
                >
                  <BlueskyIcon />
                </IconWrapper>
              ) : (
                <Icon
                  name={item.icon as any}
                  color={item.match ? '$darkBlue10' : '$grey8'}
                  size='$5'
                />
              )
            }
          />
        </Group.Item>
      ))}
    </Group>
  )
}

const MemberNavigationButtons = () => {
  const params = useParams()

  const actorIdMatch = encodeURIComponent(params.actorId ?? '')
  const generalMatch = useMatch(`/${actorIdMatch}/settings`)
  const notificationsMatch = useMatch(`/${actorIdMatch}/settings/notifications`)
  const leaveMatch = useMatch(`/${actorIdMatch}/settings/leave`)

  const activeIndex = useMemo(() => {
    if (generalMatch) return 0
    if (notificationsMatch) return 1
    if (leaveMatch) return 2
    return undefined
  }, [generalMatch, notificationsMatch, leaveMatch])

  return (
    <Group
      activeIndex={activeIndex}
      gap='$2'
      fullWidthHighlight
      highlightedElementProps={{ borderRadius: '$6' }}
    >
      <Group.Item index={0}>
        <NavItem
          href={`/${params.actorId}/settings`}
          isActive={generalMatch !== null}
          label='Membership'
          icon={
            <Icon
              name='home-filled'
              color={generalMatch ? '$darkBlue10' : '$grey8'}
              size='$5'
            />
          }
        />
      </Group.Item>

      <Group.Item index={1}>
        <NavItem
          href={`/${params.actorId}/settings/notifications`}
          isActive={notificationsMatch !== null}
          label='Notifications'
          icon={
            <Icon
              name='mail-inbox-filled'
              color={notificationsMatch ? '$darkBlue10' : '$grey8'}
              size='$5'
            />
          }
        />
      </Group.Item>

      <Group.Item index={2}>
        <NavItem
          href={`/${params.actorId}/settings/leave`}
          isActive={leaveMatch !== null}
          label='Leave'
          icon={
            <Icon
              name='door-arrow-left-filled'
              color={leaveMatch ? '$darkBlue10' : '$grey8'}
              size='$5'
            />
          }
        />
      </Group.Item>
    </Group>
  )
}

const NavigationButtons = ({
  membershipLevel,
  sponsorshipApproved,
}: {
  membershipLevel: GuildMembershipLevel
  sponsorshipApproved: boolean | null
}) => {
  const params = useParams()

  return (
    <YStack gap='$10'>
      <Link
        href={`/${params.actorId}`}
        hoverStyle={{ textDecorationLine: 'none' }}
      >
        <XStack alignItems='center' gap='$3' paddingLeft='$5'>
          <Icon name='arrow-left' color='$grey8' />
          <Text fontWeight='$6' color='$grey8'>
            Guild Settings
          </Text>
        </XStack>
      </Link>

      {membershipLevel === 'ORGANIZER' ? (
        <OrganizerNavigationButtons
          sponsorshipApproved={sponsorshipApproved}
        />
      ) : (
        <MemberNavigationButtons />
      )}
    </YStack>
  )
}

const NavigationStack = ({
  membershipLevel,
  sponsorshipApproved,
}: {
  membershipLevel: GuildMembershipLevel
  sponsorshipApproved: boolean | null
}) => {
  return (
    <Routes>
      <Route
        path='*'
        element={
          membershipLevel === 'ORGANIZER' ? (
            <Routes>
              <Route index element={<GuildEditGeneralScreen />} />
              <Route path='members' element={<GuildEditMembersScreen />} />
              <Route path='message' element={<GuildSettingsMessageScreen />} />

              <Route path='network' element={<GuildEditNetworkScreen />} />

              <Route
                path='monetization'
                element={<GuildSettingsMonetizationScreen />}
              />

              {sponsorshipApproved && (
                <Route
                  path='sponsorship'
                  element={<GuildSettingsSponsorshipScreen />}
                />
              )}

              <Route path='bluesky' element={<GuildSettingsBlueskyScreen />} />
              <Route path='slack' element={<GuildSettingsSlackScreen />} />
              <Route path='meetup' element={<GuildEditMeetupScreen />} />

              <Route
                path='notifications'
                element={<GuildSettingsNotificationsScreen />}
              />

              <Route path='embeds' element={<GuildSettingsEmbedsScreen />} />
            </Routes>
          ) : (
            <Routes>
              <Route index element={<GuildSettingsMyMembershipScreen />} />

              <Route
                path='notifications'
                element={<GuildSettingsNotificationsScreen />}
              />

              <Route path='leave' element={<GuildSettingsLeaveScreen />} />
            </Routes>
          )
        }
      />
    </Routes>
  )
}

export default GuildSettingsNavigator
